import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { ErrorBlockComponent, PageActionModalComponent, TimeFieldComponent } from '@clinic-platform/ui-components';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import {
  ClinicOperationalSettingsDto,
  ClinicProcedureAssignmentDto,
  SaveClinicSettingsRequest,
} from '../data-access/settings.models';
import {
  TimeZoneOption,
  buildTimeZoneCatalog,
  findTimeZoneRegion as findTimeZoneRegionInCatalog,
} from './settings-time-zone.util';

export type ClinicOperationalSettingsFormValue = {
  workingDayStart: string;
  workingDayEnd: string;
  slotDurationMinutesOverride: number;
  timeZoneOverride: string | null;
  procedureTypeIds: string[];
};

@Component({
  selector: 'app-clinic-operational-settings-modal',
  standalone: true,
  imports: [
    PageActionModalComponent,
    TimeFieldComponent,
    ReactiveFormsModule,
    ErrorBlockComponent,
    TranslatePipe,
  ],
  template: `
    <lib-page-action-modal
      [isOpen]="isOpen && !!clinic"
      [title]="clinicLabel + ' settings'"
      [subtitle]="'Adjust working day, slot override, and assigned ' + procedureTypeLabelPluralLower + '.'"
      width="640px"
      [canClose]="!isSaving"
      [canSubmit]="canSubmit"
      (close)="closeRequested()"
      (submit)="saveRequested()"
    >
      @if (clinic) {
        <form [formGroup]="form" (ngSubmit)="saveRequested()" class="clinic-operational-modal__grid">
          @if (pendingSave) {
            <div class="clinic-operational-modal__feedback">
              Operation: {{ pendingSave.operationId }}
              <div class="clinic-operational-modal__actions">
                <button type="button" class="clinic-operational-modal__action" [disabled]="isSaving" (click)="recover.emit()">Check result</button>
                <button type="button" class="clinic-operational-modal__action" [disabled]="isSaving" (click)="retrySave.emit()">Retry same save</button>
              </div>
            </div>
          }
          <fieldset class="clinic-operational-modal__fields" [disabled]="isSaving || !!pendingSave || saveBlocked">
          <label>
            Working day start
            <lib-time-field inputId="clinic-workingDayStart" formControlName="workingDayStart" data-modal-primary-focus="true" />
          </label>
          <label>
            Working day end
            <lib-time-field inputId="clinic-workingDayEnd" formControlName="workingDayEnd" />
          </label>
          <label>
            Slot duration override (0 = inherit)
            <input type="number" min="0" max="180" formControlName="slotDurationMinutesOverride" />
          </label>
          <label>
            Scheduling timezone mode
            <select [value]="selectedTimeZoneMode" (change)="onTimeZoneModeChange($any($event.target).value)">
              <option value="inherit">Inherit company timezone</option>
              <option value="override">Use clinic-specific timezone</option>
            </select>
          </label>

          @if (selectedTimeZoneMode === 'override') {
            <label>
              Scheduling timezone region
              <select [value]="selectedTimeZoneRegion" (change)="onTimeZoneRegionChange($any($event.target).value)">
                @for (region of timeZoneRegions; track region) {
                  <option [value]="region" [selected]="region === selectedTimeZoneRegion">{{ region }}</option>
                }
              </select>
            </label>

            <label>
              Scheduling timezone
              <select
                formControlName="timeZoneOverride"
                [disabled]="!selectedTimeZoneRegion"
                (change)="onTimeZoneValueChange($any($event.target).value)"
              >
                <option value="">Select timezone</option>
                @for (item of timeZoneOptions; track item.id) {
                  <option [value]="item.id">{{ item.label }}</option>
                }
              </select>
            </label>
          } @else {
            <div class="clinic-operational-modal__meta clinic-operational-modal__meta--inline">
              Effective timezone: {{ clinic.timeZoneInherited ?? clinic.timeZone }}
            </div>
          }

          <div class="clinic-operational-modal__procedure-box">
            <h4>Assigned {{ procedureTypeLabelPluralLower }}</h4>
            @if (isAssignmentsLoading) {
              <div class="clinic-operational-modal__meta">Loading {{ procedureTypeLabelPluralLower }}...</div>
            } @else if (!procedureAssignments.length) {
              <div class="clinic-operational-modal__meta">No {{ procedureTypeLabelPluralLower }} found in company catalog.</div>
            } @else {
              <div class="clinic-operational-modal__procedure-list">
                @for (item of procedureAssignments; track item.procedureTypeId) {
                  <label class="clinic-operational-modal__procedure-item">
                    <input
                      type="checkbox"
                      [checked]="isProcedureAssigned(item.procedureTypeId)"
                      (change)="toggleProcedureAssignment(item.procedureTypeId, $any($event.target).checked)"
                    />
                    <span>
                      {{ item.procedureName }} ({{ item.defaultDurationMinutes }} min)
                      @if (!item.isActive) {
                        - Inactive
                      }
                    </span>
                  </label>
                }
              </div>
            }
          </div>

          </fieldset>
          @if (saveError) {
            <div role="alert" class="clinic-operational-modal__feedback">
              <lib-error-block variant="error" [message]="saveError | t" />
            </div>
          }
          <div class="clinic-operational-modal__actions">
            <button type="button" class="clinic-operational-modal__action" [disabled]="isSaving" (click)="closeRequested()">
              Cancel
            </button>
            <button
              type="submit"
              class="clinic-operational-modal__action clinic-operational-modal__action--primary"
              [disabled]="!canSubmit"
            >
              {{ isSaving ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </form>
      }
    </lib-page-action-modal>
  `,
  styles: [`
    .clinic-operational-modal__fields {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 12px;
      min-width: 0;
      grid-column: 1 / -1;
      border: 0;
      margin: 0;
      padding: 0;
    }

    .clinic-operational-modal__feedback {
      grid-column: 1 / -1;
      overflow-wrap: anywhere;
      color: #b91c1c;
      font-size: 13px;
    }

    .clinic-operational-modal__grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 12px;
      align-items: end;
    }

    .clinic-operational-modal__grid label {
      display: flex;
      flex-direction: column;
      gap: 6px;
      font-size: 13px;
      color: #374151;
      font-weight: 600;
    }

    .clinic-operational-modal__grid input[type='number'],
    .clinic-operational-modal__grid select {
      height: 40px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 0 10px;
      box-sizing: border-box;
      font-size: 14px;
      color: #111827;
      background: #ffffff;
    }

    .clinic-operational-modal__meta {
      margin-top: 12px;
      color: #6b7280;
      font-size: 13px;
    }

    .clinic-operational-modal__meta--inline {
      margin-top: 0;
      align-self: end;
      padding-bottom: 8px;
    }

    .clinic-operational-modal__procedure-box {
      grid-column: 1 / -1;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 10px;
      background: #f9fafb;
    }

    .clinic-operational-modal__procedure-box h4 {
      margin: 0 0 8px;
      font-size: 14px;
      color: #111827;
    }

    .clinic-operational-modal__procedure-list {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
      gap: 8px 12px;
    }

    .clinic-operational-modal__procedure-item {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
      color: #374151;
      font-weight: 500;
    }

    .clinic-operational-modal__actions {
      grid-column: 1 / -1;
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 8px;
    }

    .clinic-operational-modal__action {
      height: 32px;
      border-radius: 8px;
      border: 1px solid #2563eb;
      background: #ffffff;
      color: #2563eb;
      padding: 0 10px;
      font-size: 13px;
      cursor: pointer;
    }

    .clinic-operational-modal__action:disabled {
      cursor: not-allowed;
      opacity: 0.65;
    }

    .clinic-operational-modal__action--primary {
      background: #2563eb;
      color: #ffffff;
    }

    @media (max-width: 900px) {
      .clinic-operational-modal__grid,
      .clinic-operational-modal__procedure-list {
        grid-template-columns: 1fr;
      }

      .clinic-operational-modal__actions {
        flex-direction: column-reverse;
      }

      .clinic-operational-modal__action {
        width: 100%;
      }
    }
  `],
})
export class ClinicOperationalSettingsModalComponent implements OnChanges
{
  @Input() public isOpen = false;
  @Input() public clinic: ClinicOperationalSettingsDto | null = null;
  @Input() public clinicLabel = 'Clinic';
  @Input() public procedureTypeLabelPluralLower = 'procedure types';
  @Input() public procedureAssignments: ClinicProcedureAssignmentDto[] = [];
  @Input() public isAssignmentsLoading = false;
  @Input() public assignmentsReady = false;
  @Input() public isSaving = false;
  @Input() public pendingSave: SaveClinicSettingsRequest | null = null;
  @Input() public saveError: string | null = null;
  @Input() public saveBlocked = false;
  @Input() public defaultTimeZoneRegion = '';

  @Output() public readonly close = new EventEmitter<void>();
  @Output() public readonly save = new EventEmitter<ClinicOperationalSettingsFormValue>();
  @Output() public readonly recover = new EventEmitter<void>();
  @Output() public readonly retrySave = new EventEmitter<void>();

  private readonly formBuilder = inject(FormBuilder);
  private readonly timeZoneCatalog = buildTimeZoneCatalog();

  protected readonly timeZoneRegions = Array.from(this.timeZoneCatalog.keys());
  protected selectedTimeZoneMode: 'inherit' | 'override' = 'inherit';
  protected selectedTimeZoneRegion = '';
  protected selectedProcedureTypeIds: string[] = [];

  protected readonly form = this.formBuilder.group({
    workingDayStart: this.formBuilder.nonNullable.control('09:00', [Validators.required]),
    workingDayEnd: this.formBuilder.nonNullable.control('18:00', [Validators.required]),
    slotDurationMinutesOverride: this.formBuilder.nonNullable.control(0, [
      Validators.required,
      Validators.min(0),
      Validators.max(180),
    ]),
    timeZoneOverride: this.formBuilder.nonNullable.control(''),
  });

  protected get timeZoneOptions(): TimeZoneOption[] {
    return this.selectedTimeZoneRegion
      ? (this.timeZoneCatalog.get(this.selectedTimeZoneRegion) ?? [])
      : [];
  }

  protected get canSubmit(): boolean {
    return !this.form.invalid && !this.isSaving && !this.pendingSave && !this.saveBlocked && !this.isAssignmentsLoading && this.assignmentsReady;
  }

  public ngOnChanges(changes: SimpleChanges): void
  {
    if (changes['isOpen']?.currentValue === true || changes['clinic'] || changes['pendingSave']?.currentValue) {
      this.resetFromClinic();
    }

    if (changes['procedureAssignments']) {
      this.selectedProcedureTypeIds = this.pendingSave?.procedureTypeIds ?? this.procedureAssignments
        .filter((assignment) => assignment.assigned)
        .map((assignment) => assignment.procedureTypeId);
    }
  }

  protected onTimeZoneRegionChange(region: string): void
  {
    this.selectedTimeZoneRegion = region;
    if (this.selectedTimeZoneMode !== 'override') {
      return;
    }

    if (!region) {
      this.form.controls.timeZoneOverride.setValue('');
      return;
    }

    const current = this.form.controls.timeZoneOverride.value;
    if (current && !this.hasTimeZoneInRegion(region, current)) {
      this.form.controls.timeZoneOverride.setValue('');
    }
  }

  protected onTimeZoneModeChange(mode: string): void
  {
    this.selectedTimeZoneMode = mode === 'override' ? 'override' : 'inherit';

    if (this.selectedTimeZoneMode === 'inherit') {
      this.form.controls.timeZoneOverride.setValue('');
      if (this.clinic) {
        this.selectedTimeZoneRegion = this.findTimeZoneRegion(
          this.clinic.timeZoneInherited ?? this.clinic.timeZone,
        );
      }
      return;
    }

    const current = this.form.controls.timeZoneOverride.value;
    if (current) {
      this.selectedTimeZoneRegion = this.findTimeZoneRegion(current);
      return;
    }

    this.selectedTimeZoneRegion = this.clinic?.timeZoneOverride
      ? this.findTimeZoneRegion(this.clinic.timeZoneOverride)
      : this.defaultTimeZoneRegion;
  }

  protected onTimeZoneValueChange(timeZone: string): void
  {
    this.form.controls.timeZoneOverride.setValue(timeZone);
    this.selectedTimeZoneRegion = timeZone ? this.findTimeZoneRegion(timeZone) : '';
  }

  protected isProcedureAssigned(procedureTypeId: string): boolean
  {
    return this.selectedProcedureTypeIds.includes(procedureTypeId);
  }

  protected toggleProcedureAssignment(procedureTypeId: string, checked: boolean): void
  {
    if (checked) {
      if (!this.selectedProcedureTypeIds.includes(procedureTypeId)) {
        this.selectedProcedureTypeIds = [...this.selectedProcedureTypeIds, procedureTypeId];
      }
      return;
    }

    this.selectedProcedureTypeIds = this.selectedProcedureTypeIds.filter(
      (id) => id !== procedureTypeId,
    );
  }

  protected closeRequested(): void
  {
    if (this.isSaving) {
      return;
    }
    this.close.emit();
  }

  protected saveRequested(): void
  {
    if (!this.canSubmit) {
      return;
    }

    const value = this.form.getRawValue();
    const workingDayStart = value.workingDayStart.trim();
    const workingDayEnd = value.workingDayEnd.trim();
    const slotDurationMinutesOverride = value.slotDurationMinutesOverride;
    const timeZoneOverride =
      this.selectedTimeZoneMode === 'override'
        ? value.timeZoneOverride.trim()
        : '';

    if (
      !this.isValidTime(workingDayStart) ||
      !this.isValidTime(workingDayEnd) ||
      !Number.isInteger(slotDurationMinutesOverride) ||
      slotDurationMinutesOverride < 0 ||
      slotDurationMinutesOverride > 180 ||
      (this.selectedTimeZoneMode === 'override' && !timeZoneOverride)
    ) {
      return;
    }

    this.save.emit({
      workingDayStart,
      workingDayEnd,
      slotDurationMinutesOverride,
      timeZoneOverride: timeZoneOverride || null,
      procedureTypeIds: this.selectedProcedureTypeIds,
    });
  }

  private resetFromClinic(): void
  {
    if (!this.clinic) {
      this.selectedTimeZoneMode = 'inherit';
      this.selectedTimeZoneRegion = '';
      this.selectedProcedureTypeIds = [];
      this.form.reset({
        workingDayStart: '09:00',
        workingDayEnd: '18:00',
        slotDurationMinutesOverride: 0,
        timeZoneOverride: '',
      }, { emitEvent: false });
      return;
    }

    this.selectedTimeZoneMode = this.clinic.timeZoneOverride ? 'override' : 'inherit';
    this.selectedTimeZoneRegion = this.clinic.timeZoneOverride
      ? this.findTimeZoneRegion(this.clinic.timeZoneOverride)
      : this.findTimeZoneRegion(this.clinic.timeZoneInherited ?? this.clinic.timeZone);
    this.form.reset({
      workingDayStart: this.clinic.workingDayStart,
      workingDayEnd: this.clinic.workingDayEnd,
      slotDurationMinutesOverride: this.clinic.slotDurationMinutesOverride ?? 0,
      timeZoneOverride: this.clinic.timeZoneOverride ?? '',
    }, { emitEvent: false });
    if (this.pendingSave) {
      this.form.patchValue({ ...this.pendingSave, timeZoneOverride: this.pendingSave.timeZoneOverride ?? '' }, { emitEvent: false });
      this.selectedTimeZoneMode = this.pendingSave.timeZoneOverride ? 'override' : 'inherit';
      this.selectedTimeZoneRegion = this.findTimeZoneRegion(this.pendingSave.timeZoneOverride || this.clinic.timeZoneInherited || this.clinic.timeZone);
      this.selectedProcedureTypeIds = [...this.pendingSave.procedureTypeIds];
    }
  }

  private findTimeZoneRegion(timeZone: string): string
  {
    return findTimeZoneRegionInCatalog(this.timeZoneCatalog, timeZone);
  }

  private hasTimeZoneInRegion(region: string, timeZone: string): boolean
  {
    return (this.timeZoneCatalog.get(region) ?? []).some((item) => item.id === timeZone);
  }

  private isValidTime(value: string): boolean
  {
    return /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
  }
}
